import { DateRange } from "@mui/x-date-pickers-pro";
import { cloneDeep } from "lodash";
import { useContext, useState } from "react";

import { CategoryFieldDef, GridData, GridRow, NumberFormatTypes, ReportGridBaseData, ValueFieldDef } from "../../../../../components/ReportGrid/ReportGrid.types";
import { AppContext } from "../../../../../core/context/appContextProvider";
import { getAllArkAccounts } from "../../../../../services/arkGL.service";
import { DateTimeFormat } from "../../../../../utils/helpers/format.helper";
import { awaitReactUpdate } from "../../../../../utils/helpers/timeoutFunctions";
import { useEffectAsync } from "../../../../../utils/hooks/useEffectAsync.hook";
import { Account, AccountHierarchyItem, AccountNumber, createAccountAttrributeGridDataSums, createAccountChildrenSums, DateColumnInfo, EndBalanceRange, FSDisplayNotSet, getAccountCategories, getAccountParentHierarchy, getFieldDefColumns, getLineItemsForDateCol, getTrialBalanceDetailsDataNew, GlStatuses, GridCallerData, insertMissingReportAccount, mergeSameFsNameToParent, ReportAccount, StartBalanceRange, suppressGridRowsIfZero,TrialBalanceDetailsParams } from "../shared";


export type BalanceSheetParams = {
    startDate: Date, 
    endDate: Date, 
    accounts: string[],
    ledgerIDs: string[], 
    glStatus: GlStatuses[],
    attributes: string[],
    fsName?: string,
    summaryRow?: boolean,
    isCustom?: boolean,
  }


export const useBalanceSheet = (
    params: BalanceSheetParams, 
    reportView: string, 
    suppressIfZero: boolean, 
    dateRange: DateRange<Date>,
    funds: any,
    attributes: any,
    viewFilters: any,
    setIsLoading: (isLoading: boolean) => void,
    handleValueLinkButtonClick: (gridRow: GridRow, valueFieldOrder: ValueFieldDef, dateColInfo: DateColumnInfo[], isCustom: boolean, currentDecimals: number) => void,
    onDataGridChange: (gridData: GridData, exportName: string) => void,
    handleDateRangeChange: (dateRange: DateRange<Date>, clearGrid: boolean) => void
) => {
    const { state } = useContext(AppContext);
    const clientId = state.loginUser.clientId;

    const [accounts, setAccounts] = useState<Account[]>([]);
    const [categoryFieldOrder, setCategoryFieldOrder] = useState<CategoryFieldDef[]>([]);
    const [currentCurrency, setCurrentCurrency] = useState('');
    const [currentDecimals, setCurrentDecimals] = useState(-1);
    const [dataFieldOrder, setDataFieldOrder] = useState<ValueFieldDef[]>([]);
    const [footerRollUp, setFooterRollUp] = useState(true);
    const [hideUseerCategorySelector, setHideUseerCategorySelector] = useState(true);
    const [pinActionColumn, setPinActionColumn] = useState<boolean>(true);
    const [reportGridBaseData, setReportGridBaseData] = useState<ReportGridBaseData[]>([]);
    const [dateColInfosResponse, setDateColInfosResponse] = useState<DateColumnInfo[]>([]);
    const [dateColInfosResponseArray, setDateColInfosResponseArray] = useState<DateColumnInfo[]>([]);
    const [rollupMode, setRollupMode] = useState(false);
    const [showGroups, setShowGroups] = useState(true);
    const [showMonthlyGroup, setShowMonthlyGroup] = useState(false);
    const [showQuarterlyGroup, setShowQuarterlyGroup] = useState(false);
    const [showYearlyGroup, setShowYearlyGroup] = useState(false);
    const [reportAccountsResponse, setReportAccountsResponse] = useState<ReportAccount[]>([]);
    const [firstLineItemDate, setFirstLineItemDate] = useState<Date>();
    const [buildReportDataGridCounter, setBuildReportDataGridCounter] = useState(0);

    useEffectAsync(async (isCanceled) => {
        setIsLoading(true);
        
        const trialBalanceResponse = await getTrialBalanceDetailsDataNew(params, isCanceled);

        if(!isCanceled() && !trialBalanceResponse ) return;

        const arkAccountsResponse = await getAllArkAccounts();

        if(!isCanceled() && !arkAccountsResponse ) return;


        const { reportAccounts, firstLineItemDate, decimals, currency } = trialBalanceResponse!;

        setCurrentCurrency(currency);
        setCurrentDecimals(decimals);
        setReportAccountsResponse(reportAccounts);
        
        let tempAccounts = arkAccountsResponse.items as Account[];

        tempAccounts = tempAccounts.sort((a,b) => a.name < b.name ? -1 : 1);

        if(params.startDate) { 
            setFirstLineItemDate(params.startDate);
        } else {
            setFirstLineItemDate(firstLineItemDate);
            handleDateRangeChange([firstLineItemDate, params.endDate], false);
        }

        setAccounts(tempAccounts);
        setIsLoading(false);
        setBuildReportDataGridCounter(buildReportDataGridCounter+1);
    }, [suppressIfZero]);

    useEffectAsync( async () => {
        if(buildReportDataGridCounter === 0) return;

        setIsLoading(true);
        await awaitReactUpdate();

        const currencyFormat = `Currency.${currentDecimals}` as  NumberFormatTypes;        
        const accountHierarchy = getAccountParentHierarchy(accounts);

        insertMissingReportAccount(accountHierarchy, reportAccountsResponse, params.attributes, currentDecimals);      

        if(suppressIfZero) suppressGridRowsIfZero(reportAccountsResponse, true);

        const currentReportCategoryFieldDefs = getAccountCategories(accountHierarchy,  'account');
       

        let fieldDefsArray: ValueFieldDef[] = [];
        let dateColInfosArray: DateColumnInfo[] = [];
      
        if(dataFieldOrder.length){
            fieldDefsArray = dataFieldOrder;
            dateColInfosArray = dateColInfosResponseArray;
        } else {
            const startDate = dateRange[0] ? dateRange[0] : firstLineItemDate;
            const { fieldDefs, dateColInfos } = getFieldDefColumns(startDate!, dateRange[1]!, currencyFormat, true, false, false, false, false, false, true, false);
      
            fieldDefsArray = fieldDefs;
            dateColInfosArray = dateColInfos;
            setDateColInfosResponseArray(dateColInfos);
          }
          
          const filterDateColInfos: DateColumnInfo[] = [];
  
          dateColInfosArray.map((column) => {
            if(column.category === undefined){
                filterDateColInfos.push(column);
            }
            if(showMonthlyGroup){
                column.category === "Months" ? filterDateColInfos.push(column) : "";
            }
            if(showQuarterlyGroup){
                column.category === "Quarterly" ? filterDateColInfos.push(column) : "";
            }
            if(showYearlyGroup){
                column.category === "Yearly" ? filterDateColInfos.push(column) : "";
            }
          });
      
          fieldDefsArray.map((field) => {
            if(showMonthlyGroup === true && field.category === "Months"){
              field.visible = showMonthlyGroup;
            }
            if(showQuarterlyGroup === true && field.category === "Quarterly"){
              field.visible = showQuarterlyGroup;
            }
            if(showYearlyGroup === true && field.category === "Yearly"){
              field.visible = showYearlyGroup;
            }
          });

          const reportGridData = buildReportGridData(reportAccountsResponse, filterDateColInfos, currentCurrency, accountHierarchy, currentReportCategoryFieldDefs);

          const suppressed = reportGridData.filter((row: any) => {
              const categoryPropName = "account_name_2";
        
              const dataPropsValue = Object.entries(row.dataProps);
              const child = row.categoryProps && row.categoryProps[categoryPropName] !== undefined;
              
              return child || !dataPropsValue.every(([key, value]) => (key === "Account_Number") || value === 0);
            });

        setCategoryFieldOrder(cloneDeep(currentReportCategoryFieldDefs));
        setDataFieldOrder(cloneDeep(fieldDefsArray));
        setDateColInfosResponse(cloneDeep(dateColInfosArray));
        setPinActionColumn(true);
        setReportGridBaseData(suppressIfZero ? cloneDeep(suppressed) : cloneDeep(reportGridData)); 

        setIsLoading(false);
    }, [buildReportDataGridCounter, showMonthlyGroup, showQuarterlyGroup, showYearlyGroup]);


    function buildReportGridData(
        reportAccounts: ReportAccount[], 
        dateColInfos: DateColumnInfo[], 
        responseCurrency: string, 
        accountHierarchy: AccountHierarchyItem[],
        categoryFieldDefs: CategoryFieldDef[]
    ): ReportGridBaseData[] {
        const incomeAttrib = attributes.find((a:any) => a.type === 'Income');
        const expenseAttrib = attributes.find((a:any) => a.type === 'Expense');
        const gainLossAttrib = attributes.find((a:any) => a.type === 'Gain/Loss');
        const partnersCap = attributes.filter((a:any) => a.type === 'Partners Capital');

        const partnersCapitalCatStr = '3|Partners Capital';
        const cumulativeNetInvestmentIncomeCatStr = 'ZZ1|Cumulative net investment income/(loss)';
        const cumulativeNetRealizedGainLossCatStr = 'ZZ2|Cumulative net realized gain/(loss) on investments';
        const cumulativeNetUnrealizedGainLossCatStr = 'ZZ3|Cumulative net unrealized gain/(loss) on investments';

        const reportGridData: ReportGridBaseData[] = [];
        const exAndGLreportGridData: ReportGridBaseData[] = [];

        const allAccounts = reportAccounts.filter(ra => ra.attributeId !== expenseAttrib.id && ra.attributeId !== incomeAttrib.id); // start with accounts without Income/Expenses
        const accountsWithIncomeExpenses = reportAccounts.filter(ra => ra.attributeId === incomeAttrib.id || ra.attributeId === expenseAttrib.id);

        if(accountsWithIncomeExpenses?.length) {
            const firstAccount = accountsWithIncomeExpenses[0];

            accountsWithIncomeExpenses.filter(acct => acct.accountId !== firstAccount.accountId).forEach(acct => {
                if(expenseAttrib.id ===  acct.attributeId) {
                    // @ts-ignore
                    acct.lineItems.forEach(li => li['subtract'] = true );
                }

                firstAccount.lineItems = firstAccount.lineItems.concat(acct.lineItems);
            });

            firstAccount.lineItems.sort((a,b) => DateTimeFormat.compareDateOnly(a.date, b.date));

            allAccounts.push(firstAccount);
        }

        for(let i=0; i<allAccounts.length; i++) {
            const account = allAccounts[i];

            let categoryProps = {} as any;
            const dataProps = {} as any;            
            const foundIncome = incomeAttrib.id ===  account.attributeId;
            const foundExpense = expenseAttrib.id ===  account.attributeId;
            const foundGainLoss = gainLossAttrib.id ===  account.attributeId;

            if(!foundIncome &&  !foundExpense && !foundGainLoss) {
                categoryProps = getAccountCategoryProperties(account.accountId, accountHierarchy, categoryFieldDefs);
            } else {
                let cat: string;
                
                if(foundIncome || foundExpense) {
                    cat = cumulativeNetInvestmentIncomeCatStr;
                } else {
                    const acctName = account.accountName.toLowerCase();

                    // since realized is in unrealized we need to check both
                    if(acctName.includes('realized') && !acctName.includes('unrealized')) {
                        cat = cumulativeNetRealizedGainLossCatStr;
                    } else {
                        cat = cumulativeNetUnrealizedGainLossCatStr;
                    }
                }

                categoryProps.attributeId = partnersCap.id;
                categoryProps.attributeName = partnersCapitalCatStr;
                categoryProps.account_id_1 = undefined;
                categoryProps.account_name_1 = cat;
                categoryProps.account_id_2 = undefined;
                categoryProps.account_name_2 = undefined;
                categoryProps.account_id_3 = undefined;
                categoryProps.account_name_3 = undefined;
            }

            // Set the dataProps based on the account api response data
            dataProps[EndBalanceRange] = account.endBalance;
            dataProps[StartBalanceRange] = account.startBalance;
            dataProps[AccountNumber] = account.accountNo;

            // Filter through each column and pass it the date range selected "dc" to filter items in range and type
            // getLineItems is part of the shared.tsx
            dateColInfos.filter(cd => 
                cd.name !== StartBalanceRange && 
                cd.name !== EndBalanceRange && 
                cd.numberFormat !== 'Text'
            ).forEach(dc => {
                const lineItemsInRange = getLineItemsForDateCol(account.lineItems, dc);
                let lIvalue:number;

                if(foundIncome || foundExpense) {
                    lIvalue=0;

                    lineItemsInRange.forEach(lineItem => {
                        // @ts-ignore
                        if(lineItem.subtract) {
                            lIvalue -= lineItem.amount;
                        } else {
                            lIvalue += lineItem.amount;
                        }
                    });

                    dataProps[EndBalanceRange] = lIvalue;

                } else {
                    lIvalue = lineItemsInRange.reduce((prevVal, lineItem) => prevVal + lineItem.amount, 0);
                }

                dataProps[dc.name] = lIvalue;
            }); 

            let callerData: GridCallerData;

            if(foundIncome || foundExpense) {
                callerData = { reportAccounts: accountsWithIncomeExpenses };
            } else {
                callerData = { reportAccounts: [account] };
            }
    
            const gridData = {
                itemId: account.accountId,
                currencyCode: responseCurrency,
                categoryProps: categoryProps,
                dataProps: dataProps,
                callerData: callerData,
                decimals: currentDecimals
            } as ReportGridBaseData;
    

            if(!foundIncome && !foundExpense && !foundGainLoss) {
                reportGridData.push(gridData);
            } else {
                exAndGLreportGridData.push(gridData);
            }
        }

        const filters = viewFilters.filteredAttributesFilter.filter((a:any) => a.id !== gainLossAttrib.id  && a.id !== incomeAttrib.id && a.id !== expenseAttrib.id);

        createAccountAttrributeGridDataSums(reportGridData, filters, dateColInfos, currentCurrency);
        createAccountChildrenSums(reportGridData, dateColInfos, categoryFieldDefs, currentCurrency, false);

        const partnersCapitalRow = reportGridData.find(rgd => rgd.categoryProps.attributeName === partnersCapitalCatStr && !rgd.categoryProps.account_name_1)!;
        const numberCols = dateColInfos.filter(c => c.numberFormat !== 'Text');


        exAndGLreportGridData.forEach(rgd => {
            numberCols.forEach(col => {
                partnersCapitalRow.dataProps[col.name] += rgd.dataProps[col.name];
            });

            partnersCapitalRow.callerData.reportAccounts = partnersCapitalRow.callerData.reportAccounts.concat(rgd.callerData.reportAccounts);

            reportGridData.push(rgd);
        });

        return reportGridData;
    }

    function getAccountCategoryProperties(reportAccountId: string, accountHierarchy: AccountHierarchyItem[], categoryFieldDefs: CategoryFieldDef[]): any {
        const acct = accountHierarchy!.find(a => a.accountId === reportAccountId)!;
        const attrib = attributes.find((f:any) => f.id === acct.attributeId);
        const categoryProps = {} as any;
  
        categoryProps.attributeId = attrib.id,
        categoryProps.attributeName = getCategoryNameWithOrder(attrib.type);
        categoryFieldDefs = categoryFieldDefs.filter(c => c.id !== 'attributeId');

        categoryFieldDefs.forEach((cat, index) => {
            const parentAccount = acct.parentAccounts[index];
  
            // @ts-ignore: expression error
            categoryProps[cat.id] = parentAccount?.id;
            // @ts-ignore: expression error
            categoryProps[cat.name] = parentAccount?.number ? parentAccount?.number + " - " + parentAccount?.name : parentAccount?.name;
        });
  
        return categoryProps;
    }

    function getCategoryNameWithOrder(name: string) {
        let order: string = '';
  
        switch(name) {
          case "Assets":
          case 'Income':
            order = '1'; 
            break;
          case 'Liabilities':
          case 'Expense':
            order = '2';
            break;
          case 'Partners Capital':
          case 'Gain/Loss':
            order = '3';
            break;
        }
  
        return `${order}|${name}`;
      }


    function handleCategoryFieldOrderChange(updatedCategoryFieldOrder: CategoryFieldDef[]) {
        setCategoryFieldOrder(cloneDeep(updatedCategoryFieldOrder));
    }

    function handleDataFieldOrderChange(updatedDataFieldOrder: ValueFieldDef[]) {
        setDataFieldOrder(cloneDeep(updatedDataFieldOrder));
    }

    function handleDataGridChange(gridData: GridData) {
        onDataGridChange(gridData, `report export ${DateTimeFormat.getFormattedDate(new Date())}.csv`);
    }

    function handleValueClick(gridRow: GridRow, valueFieldOrder: ValueFieldDef) {
        handleValueLinkButtonClick(gridRow, valueFieldOrder, dateColInfosResponse, false, currentDecimals); //false because not custom   
    }

    return {
        categoryFieldOrder,
        currentCurrency,
        currentDecimals,
        dataFieldOrder,
        footerRollUp,
        handleCategoryFieldOrderChange,
        handleDataFieldOrderChange,
        handleDataGridChange,
        handleValueClick,
        hideUseerCategorySelector,
        pinActionColumn,
        reportGridBaseData,
        rollupMode,
        setShowGroups,
        setShowMonthlyGroup,
        setShowQuarterlyGroup,
        setShowYearlyGroup,
        showGroups,
        showMonthlyGroup,
        showQuarterlyGroup,
        showYearlyGroup,
    };
};